import React from 'react';

function Loader() {
  return (
    <div className="loader-screen">
      {/* Animated gradient orbs - CSS only */}
      <div className="loader-orb loader-orb--1" />
      <div className="loader-orb loader-orb--2" />
      <div className="loader-orb loader-orb--3" />

      {/* Content */}
      <div className="loader-content">
        <div className="loader-words">
          <span className="loader-word loader-word--1">Creating</span>
          <span className="loader-word loader-word--2">Something</span>
          <span className="loader-word loader-word--3 loader-word--accent">Amazing</span>
        </div>

        <div className="loader-line" />

        <p className="loader-tagline">Loading experience</p>

        <div className="loader-dots">
          <span className="loader-dot" />
          <span className="loader-dot" />
          <span className="loader-dot" />
        </div>
      </div>

      {/* Corner accents */}
      <span className="loader-corner loader-corner--tl" />
      <span className="loader-corner loader-corner--br" />

      <style>{`
        .loader-screen {
          position: fixed;
          inset: 0;
          width: 100vw;
          height: 100vh;
          background: #0a0a0a;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: hidden;
          font-family: "Poppins", sans-serif;
          contain: layout style paint;
          isolation: isolate;
        }

        /* GPU-composited orbs - only use transform & opacity */
        .loader-orb {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          will-change: transform, opacity;
        }
        .loader-orb--1 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(30, 205, 151, 0.25) 0%, transparent 70%);
          top: 10%;
          left: 15%;
          filter: blur(60px);
          animation: orbFloat1 4s ease-in-out infinite alternate;
        }
        .loader-orb--2 {
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, rgba(0, 212, 255, 0.2) 0%, transparent 70%);
          bottom: 10%;
          right: 10%;
          filter: blur(60px);
          animation: orbFloat2 5s ease-in-out infinite alternate;
        }
        .loader-orb--3 {
          width: 250px;
          height: 250px;
          background: radial-gradient(circle, rgba(168, 85, 247, 0.15) 0%, transparent 70%);
          top: 50%;
          left: 50%;
          filter: blur(50px);
          animation: orbFloat3 3.5s ease-in-out infinite alternate;
        }

        @keyframes orbFloat1 {
          from { transform: translate(0, 0) scale(1); }
          to { transform: translate(80px, -60px) scale(1.3); }
        }
        @keyframes orbFloat2 {
          from { transform: translate(0, 0) scale(1); }
          to { transform: translate(-100px, 50px) scale(0.8); }
        }
        @keyframes orbFloat3 {
          from { transform: translate(-50%, -50%) scale(1); }
          to { transform: translate(calc(-50% + 40px), calc(-50% + 60px)) scale(1.4); }
        }

        /* Content container */
        .loader-content {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
        }

        /* Words container */
        .loader-words {
          display: flex;
          gap: clamp(0.6rem, 2vw, 1.5rem);
          flex-wrap: wrap;
          justify-content: center;
          overflow: hidden;
          padding: 0.5rem 0;
        }

        .loader-word {
          font-size: clamp(2.2rem, 7vw, 5.5rem);
          font-weight: 800;
          color: #ffffff;
          text-transform: uppercase;
          letter-spacing: -2px;
          line-height: 1;
          display: inline-block;
          will-change: transform, opacity;
          animation: wordReveal 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        }
        .loader-word--1 { animation-delay: 0.1s; }
        .loader-word--2 { animation-delay: 0.25s; }
        .loader-word--3 { animation-delay: 0.4s; }

        .loader-word--accent {
          background: linear-gradient(135deg, #1ecd97, #00d4ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        @keyframes wordReveal {
          from {
            transform: translateY(100%) rotateX(-80deg);
            opacity: 0;
          }
          to {
            transform: translateY(0) rotateX(0deg);
            opacity: 1;
          }
        }

        /* After reveal, float gently */
        .loader-word--1 {
          animation: wordReveal 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) both,
                     wordFloat 2.5s ease-in-out 1s infinite alternate;
        }
        .loader-word--2 {
          animation: wordReveal 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.25s both,
                     wordFloat 3s ease-in-out 1.2s infinite alternate;
        }
        .loader-word--3 {
          animation: wordReveal 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s both,
                     wordFloat 2.8s ease-in-out 1.4s infinite alternate;
        }

        @keyframes wordFloat {
          from { transform: translateY(0); }
          to { transform: translateY(-8px); }
        }

        /* Expanding line */
        .loader-line {
          width: clamp(80px, 25vw, 250px);
          height: 3px;
          background: linear-gradient(90deg, #1ecd97, #00d4ff);
          border-radius: 2px;
          will-change: transform;
          transform-origin: center;
          animation: lineExpand 0.8s cubic-bezier(0.65, 0, 0.35, 1) 0.6s both;
        }

        @keyframes lineExpand {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }

        /* Tagline */
        .loader-tagline {
          font-size: clamp(0.75rem, 1.3vw, 1rem);
          color: #555;
          letter-spacing: 3px;
          text-transform: uppercase;
          margin: 0;
          will-change: opacity;
          animation: fadeIn 0.6s ease 0.9s both;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Bouncing dots */
        .loader-dots {
          display: flex;
          gap: 8px;
          animation: fadeIn 0.5s ease 1.1s both;
        }

        .loader-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #1ecd97;
          will-change: transform;
          animation: dotBounce 1.4s ease-in-out infinite;
        }
        .loader-dot:nth-child(1) { animation-delay: 0s; }
        .loader-dot:nth-child(2) { animation-delay: 0.15s; }
        .loader-dot:nth-child(3) { animation-delay: 0.3s; }

        @keyframes dotBounce {
          0%, 60%, 100% { transform: translateY(0) scale(1); }
          30% { transform: translateY(-12px) scale(1.2); }
        }

        /* Corner accents */
        .loader-corner {
          position: absolute;
          width: 20px;
          height: 20px;
          border: 2px solid rgba(30, 205, 151, 0.3);
          will-change: opacity;
          animation: fadeIn 1s ease 0.5s both;
        }
        .loader-corner--tl {
          top: 2rem;
          left: 2rem;
          border-right: none;
          border-bottom: none;
        }
        .loader-corner--br {
          bottom: 2rem;
          right: 2rem;
          border-left: none;
          border-top: none;
        }
      `}</style>
    </div>
  );
}

export default Loader;
